import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import api from '../../services/unified';
import type { JobAnalyticsResponse, ApplicationAnalytics } from '../../services/unified/types';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from 'recharts';

// Fallback hex colors for recharts (CSS vars don't work in all chart contexts)
const CHART_HEX = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  accent: '#d946ef',
  info: '#06b6d4',
  gray: '#9ca3af',
};

const STATUS_COLORS: Record<string, string> = {
  submitted: CHART_HEX.primary,
  reviewed: CHART_HEX.warning,
  interviewing: CHART_HEX.accent,
  offered: CHART_HEX.info,
  hired: CHART_HEX.success,
  rejected: CHART_HEX.error,
  withdrawn: CHART_HEX.gray,
};

interface DateRangeProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (start: string, end: string) => void;
}

function DateRangeSelector({ startDate, endDate, onDateRangeChange }: DateRangeProps) {
  const [activePreset, setActivePreset] = useState<number | null>(30);

  const handlePresetRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setActivePreset(days);
    onDateRangeChange(start.toISOString().split('T')[0], end.toISOString().split('T')[0]);
  };

  const presets = [
    { days: 7, label: '7D' },
    { days: 30, label: '30D' },
    { days: 90, label: '90D' },
    { days: 365, label: '1Y' },
  ];

  return (
    <div className="flex flex-col gap-3 w-full">
      {/* Preset buttons - horizontal scroll on mobile */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg overflow-x-auto mobile-scroll">
        {presets.map(({ days, label }) => (
          <button
            key={days}
            onClick={() => handlePresetRange(days)}
            className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap min-h-[44px] ${
              activePreset === days
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Date inputs - stack on mobile */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm text-gray-500 w-10 sm:w-auto">From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setActivePreset(null);
              onDateRangeChange(e.target.value, endDate);
            }}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
          />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm text-gray-500 w-10 sm:w-auto">To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setActivePreset(null);
              onDateRangeChange(startDate, e.target.value);
            }}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
          />
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({
  title,
  value,
  icon,
  trend,
  trendLabel,
  colorClass,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: number;
  trendLabel?: string;
  colorClass: string;
}) {
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${colorClass}`}>
          {icon}
        </div>
        {trend !== undefined && (
          <div
            className={`flex items-center gap-1 text-xs sm:text-sm font-medium ${
              trend >= 0 ? 'text-success' : 'text-error'
            }`}
          >
            <svg
              className={`w-3 h-3 sm:w-4 sm:h-4 ${trend < 0 ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-3 sm:mt-4">
        <p className="text-xl sm:text-3xl font-bold text-gray-900">{value}</p>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">{title}</p>
        {trendLabel && <p className="text-xs text-gray-400 mt-1 hidden sm:block">{trendLabel}</p>}
      </div>
    </div>
  );
}

// Chart Card Wrapper
function ChartCard({ title, subtitle, children, className = '' }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white rounded-xl sm:rounded-2xl border border-gray-100 overflow-hidden ${className}`}>
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-50">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs sm:text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className="p-3 sm:p-6">{children}</div>
    </div>
  );
}

// Funnel Step Component
function FunnelStep({ label, rate, color, index }: { label: string; rate: number; color: string; index: number }) {
  const percentage = (rate * 100).toFixed(1);
  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
            style={{ backgroundColor: color }}
          >
            {index + 1}
          </span>
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className="text-lg font-bold text-gray-900">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${Math.min(rate * 100, 100)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 rounded-xl shadow-lg border border-gray-100">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-semibold text-gray-900">{entry.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export function AdminAnalyticsPage() {
  const { isMobile } = useViewport();
  const [jobAnalytics, setJobAnalytics] = useState<JobAnalyticsResponse | null>(null);
  const [appAnalytics, setAppAnalytics] = useState<ApplicationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobData, appData] = await Promise.all([
        api.analytics.getJobAnalytics({ start_date: dateRange.start, end_date: dateRange.end }),
        api.analytics.getApplicationAnalytics(),
      ]);
      setJobAnalytics(jobData);
      setAppAnalytics(appData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const applicationStatusData = appAnalytics?.applications_by_status
    ? Object.entries(appAnalytics.applications_by_status).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: count,
        color: STATUS_COLORS[status] || CHART_HEX.primary,
      }))
    : [];

  const combinedTrendData =
    jobAnalytics?.view_trends?.map((viewItem, index) => ({
      date: new Date(viewItem.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      views: viewItem.count,
      applications: jobAnalytics?.application_trends?.[index]?.count || 0,
    })) || [];

  const topJobsData =
    jobAnalytics?.top_performing_jobs?.slice(0, 5).map((job) => ({
      name: job.job_title.length > 25 ? job.job_title.slice(0, 25) + '...' : job.job_title,
      fullName: job.job_title,
      views: job.views,
      applications: job.applications,
      rate: (job.conversion_rate * 100).toFixed(1),
    })) || [];

  const totalApplications = appAnalytics?.total_applications || 0;
  const conversionRate = jobAnalytics?.average_conversion_rate
    ? (jobAnalytics.average_conversion_rate * 100).toFixed(1)
    : '0';

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded-lg w-1/3 mb-2"></div>
            <div className="h-5 bg-gray-100 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-gray-100 h-[450px]"></div>
              <div className="bg-white rounded-2xl border border-gray-100 h-[450px]"></div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to load analytics</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm sm:text-base text-gray-500 mt-1">Track performance and engagement metrics</p>
          </div>
          <DateRangeSelector
            startDate={dateRange.start}
            endDate={dateRange.end}
            onDateRangeChange={(start, end) => setDateRange({ start, end })}
          />
        </div>

        {/* Key Metrics - 2 columns on mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <MetricCard
            title="Total Views"
            value={(jobAnalytics?.total_views || 0).toLocaleString()}
            colorClass="bg-primary-100 text-primary-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
          />
          <MetricCard
            title="Applications"
            value={totalApplications.toLocaleString()}
            colorClass="bg-success/10 text-success"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
          <MetricCard
            title="Active Jobs"
            value={jobAnalytics?.total_jobs || 0}
            colorClass="bg-accent-100 text-accent-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
          <MetricCard
            title="Conversion Rate"
            value={`${conversionRate}%`}
            colorClass="bg-warning/10 text-warning"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
        </div>

        {/* Charts Row 1 - Trends */}
        <ChartCard
          title="Views & Applications Trend"
          subtitle="Daily performance over selected period"
          className="col-span-full"
        >
          {combinedTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={combinedTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_HEX.primary} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={CHART_HEX.primary} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_HEX.success} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={CHART_HEX.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: 20 }} />
                <Area
                  type="monotone"
                  dataKey="views"
                  stroke={CHART_HEX.primary}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorViews)"
                  name="Views"
                />
                <Area
                  type="monotone"
                  dataKey="applications"
                  stroke={CHART_HEX.success}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorApps)"
                  name="Applications"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p>No trend data available</p>
              </div>
            </div>
          )}
        </ChartCard>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Application Status Distribution */}
          <ChartCard title="Application Status" subtitle="Distribution by current status">
            {applicationStatusData.length > 0 ? (
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
                <ResponsiveContainer width={isMobile ? "100%" : "50%"} height={isMobile ? 200 : 280}>
                  <PieChart>
                    <Pie
                      data={applicationStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={isMobile ? 50 : 70}
                      outerRadius={isMobile ? 80 : 110}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {applicationStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="w-full sm:flex-1 space-y-2 sm:space-y-3">
                  {applicationStatusData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-xs sm:text-sm text-gray-600">{item.name}</span>
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] sm:h-[280px] flex items-center justify-center text-gray-400">
                No application data available
              </div>
            )}
          </ChartCard>

          {/* Conversion Funnel */}
          <ChartCard title="Conversion Funnel" subtitle="Application stage progression rates">
            {appAnalytics?.conversion_rates ? (
              <div className="space-y-6 py-4">
                <FunnelStep
                  label="Submitted → Reviewed"
                  rate={appAnalytics.conversion_rates.submitted_to_reviewed}
                  color={CHART_HEX.primary}
                  index={0}
                />
                <FunnelStep
                  label="Reviewed → Interview"
                  rate={appAnalytics.conversion_rates.reviewed_to_interviewed}
                  color={CHART_HEX.accent}
                  index={1}
                />
                <FunnelStep
                  label="Interview → Offered"
                  rate={appAnalytics.conversion_rates.interviewed_to_offered}
                  color={CHART_HEX.warning}
                  index={2}
                />
                <FunnelStep
                  label="Offered → Hired"
                  rate={appAnalytics.conversion_rates.offered_to_hired}
                  color={CHART_HEX.success}
                  index={3}
                />
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-gray-400">
                No conversion data available
              </div>
            )}
          </ChartCard>
        </div>

        {/* Top Performing Jobs */}
        <ChartCard title="Top Performing Jobs" subtitle="Jobs with highest engagement">
          {topJobsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={topJobsData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                barCategoryGap="25%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />
                <XAxis
                  type="number"
                  domain={[0, 'dataMax']}
                  tick={{ fontSize: 13, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={180}
                  tick={{ fontSize: 13, fill: '#374151', fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: 20 }} iconType="circle" />
                <Bar dataKey="views" fill={CHART_HEX.primary} name="Views" radius={[0, 6, 6, 0]} />
                <Bar dataKey="applications" fill={CHART_HEX.success} name="Applications" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p>No job data available</p>
              </div>
            </div>
          )}
        </ChartCard>

        {/* Job Performance - Card layout on mobile, table on desktop */}
        <ChartCard title="Job Performance Details" subtitle="Detailed metrics for all jobs">
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3 -mx-1">
            {jobAnalytics?.top_performing_jobs?.map((job, index) => (
              <div key={job.job_id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{job.job_title}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                      <span>👁 {job.views.toLocaleString()}</span>
                      <span>📝 {job.applications}</span>
                      <span className="px-2 py-0.5 rounded-full bg-success/10 text-success font-semibold">
                        {(job.conversion_rate * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!jobAnalytics?.top_performing_jobs || jobAnalytics.top_performing_jobs.length === 0) && (
              <div className="py-8 text-center text-gray-400 text-sm">
                No job performance data available
              </div>
            )}
          </div>
          
          {/* Desktop: Table layout */}
          <div className="hidden sm:block overflow-x-auto -mx-6">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Job Title
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Applications
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Conversion
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {jobAnalytics?.top_performing_jobs?.map((job, index) => (
                  <tr key={job.job_id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{job.job_title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">
                      {job.views.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">{job.applications}</td>
                    <td className="px-6 py-4 text-right">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
                        {(job.conversion_rate * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
                {(!jobAnalytics?.top_performing_jobs || jobAnalytics.top_performing_jobs.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-400">
                      No job performance data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
    </AdminLayout>
  );
}
